import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTrackingPage, useSegment } from '../../components/tracking/trackingProvider';
import { WeezHeader, Button, AnchorNav, ToastContext } from '@weezevent/nacre';
import PropTypes from 'prop-types';
import Description from './description';
import LegalRepresentative from './legalRepresentative';
import GeneralInformation from './generalInformation';
import SwornStatement from './swornStatement';
import { useForm } from '@weezevent/weezjs-core';
import { requiredField, websiteField } from '../../utils/validators';
import Organization from '../../models/organization';
import MessageInformation from './messageInformation';
import useAppContext from '../../context/useAppContext';
import OrganizationInfos from '../../models/organizationInfos';
import Loading from '../../components/loading';

export default function OrganizerProfile({ organization, bank }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const { track } = useSegment();
    const [context, setContext] = useAppContext();
    const validateAddress = React.useCallback((name, address, form) => {
        const fieldAdrress = ['address1', 'city', 'country', 'zip_code'];
        if (fieldAdrress.find(field => !address[field] || !address[field].trim())) {
            return t('ticket.common.required');
        }
        if ((address['country'] === 'CA' || address['country'] === 'US') && (!address['state'] || !address['state'].trim())) {
            return t('ticket.common.required');
        }
        return true;
    }, []);

    const [organizationInfos, loadingOrganizationInfos] = OrganizationInfos.useApiModel({
        organizationId: organization.id
    });

    const validateSworn = React.useCallback((name, sworn, form) => {
        if (!sworn) {
            return t('ticket.common.required');
        }
        return true;
    }, []);

    const validatePhone = React.useCallback((name, phone, form) => {
        if (!phone) {
            return t('ticket.common.required');
        }
        const regexPhone = /^([(]?[+][0-9]{1,4}[)]?)?([ -]?[ (-]?[0-9][)]?){5,14}$/gm;
        if (!regexPhone.test(phone) || phone.length < 6) {
            return t('ticket.organiser.invalid_phone_number');
        }
        return true;
    }, []);

    const validators = React.useMemo(() => {
        return {
            name: requiredField,
            organization_type: requiredField,
            intra_community_vat: organization.business_entity.uuid === Organization.BUSINESS_ENTITY_SPAIN ? requiredField : false,
            last_name: requiredField,
            first_name: requiredField,
            phone_number: validatePhone,
            address: validateAddress,
            sworn: validateSworn,
            website: websiteField
        };
    }, [validatePhone, validateAddress, validateSworn]);

    const [formOrganisation, setFormOrganisation, , organisationErrors, organisationValidate] = useForm(
        {
            name: organization.name || '',
            organization_type: organization.organization_type || '',
            license_number: organization.license_number || '',
            intra_community_vat: organization.intra_community_vat || '',
            last_name: organization.last_name || '',
            first_name: organization.first_name || '',
            position: organization.position || '',
            address: {
                name: organization.address.zip_code || '',
                address1: organization.address.address1 || '',
                address2: organization.address.address2 || '',
                city: organization.address.city || '',
                country: organization.address.country || '',
                state: organization.address.state || '',
                zip_code: organization.address.zip_code || ''
            },
            phone_number: organization.phone_number || '',
            website: organization.website || '',
            sworn: false
        },
        validators
    );

    const [formErrors, setFormErrors] = React.useState({});
    const handleFormError = React.useCallback((field, obj = null) => {
        let errors = obj || formErrors,
            nestedFields = field.split('.');

        if (nestedFields.length > 1) {
            let key = nestedFields.shift();
            if (Object.prototype.hasOwnProperty.call(errors, key) && errors[key] instanceof Object) {
                return handleFormError(nestedFields.join('.'), errors[key]);
            }
            return false;
        }

        return Object.prototype.hasOwnProperty.call(errors, field);
    });

    React.useEffect(() => {
        organisationValidate();
    }, [formOrganisation]);

    const handleSubmit = React.useCallback(() => {
        if (!organisationValidate()) {
            return;
        }
        const { name, organization_type, license_number, intra_community_vat, last_name, first_name, position, address, phone_number, website } = formOrganisation;
        const organisation = new Organization({
            id: organization.id,
            name,
            organization_type,
            license_number,
            intra_community_vat,
            last_name,
            first_name,
            position,
            address,
            phone_number,
            website
        });

        organisation
            .save({
                organizationId: organization.id
            })
            .then(({ response }) => {
                setFormErrors({});
                const organization = new Organization(response);
                setContext({ ...context, organization });
                toast.success(t(`ticket.organiser.saveSuccess`));

                let peopleIdentify = {
                    "Organization's Name": response.name,
                    "Organization's Legal Status": response.organization_type,
                    "Representative's Last Name": response.last_name,
                    "Representative's First Name": response.first_name,
                    "Representative's Function": response.position || '',
                    'Organization Full Address': response.address.address1,
                    'Organization Postcode': response.address.zip_code,
                    'Organization City': response.address.city,
                    'Organization Country': response.address.country,
                    Cellphone: response.phone_number,
                    Website: response.website || '',
                    'Organization Info Last Update': new Date().toUTCString()
                };
                let eventInfo = {
                    'Licence Number': response.license_number || '',
                    'EU VAT Number': response.intra_community_vat || '',
                    'Page Title': 'ORGANIZER PROFILE'
                };
                track('ACTION > SAVE PROFILE', eventInfo, peopleIdentify);
            })
            .catch(error => {
                setFormErrors(error.response);
                toast.error(t(`ticket.organiser.saveError`));
            })
            .finally(() => {
                context.syncOrganization();
            });
    }, [formOrganisation, organisationValidate, context]);

    useTrackingPage('VIEW - ORGANIZER PROFILE', {
        'Page Title': 'ORGANIZER PROFILE'
    });

    if (loadingOrganizationInfos) {
        return <Loading />;
    }

    return (
        <>
            <WeezHeader
                title={t(`ticket.organiser.pageTitle`)}
                rightComponent={[
                    <Button primary key="primary" label={t('ticket.common.action.save')} onClick={handleSubmit} disabled={Object.entries(organisationErrors).length > 0} />
                ]}
            >
                {t(`ticket.organiser.descriptionTitle`)}
            </WeezHeader>
            {!bank.iban && !bank.rbc_account && <MessageInformation organization={organization} />}
            <AnchorNav
                rootId="main"
                items={[
                    {
                        id: 'organiser-form-description',
                        title: t('ticket.organiser.description.title')
                    },
                    {
                        id: 'organiser-form-legal_representative',
                        title: t('ticket.organiser.legal_representative.title')
                    },
                    {
                        id: 'organiser-form-general_information',
                        title: t('ticket.organiser.general_information.title')
                    }
                ]}
            >
                <Description
                    organization={organization}
                    formOrganisation={formOrganisation}
                    onChange={setFormOrganisation}
                    organisationErrors={organisationErrors}
                    errorHandler={handleFormError}
                    organizationInfos={organizationInfos}
                />
                <LegalRepresentative formOrganisation={formOrganisation} onChange={setFormOrganisation} organisationErrors={organisationErrors} errorHandler={handleFormError} />
                <GeneralInformation
                    formOrganisation={formOrganisation}
                    onChange={setFormOrganisation}
                    organization={organization}
                    organisationErrors={organisationErrors}
                    errorHandler={handleFormError}
                />
                <SwornStatement formOrganisation={formOrganisation} onChange={setFormOrganisation} />
            </AnchorNav>
        </>
    );
}

OrganizerProfile.propTypes = {
    organization: PropTypes.object.isRequired,
    bank: PropTypes.object.isRequired
};
